// Align, centre, place beside, and distribute (owner, 2026-10-02: "align objects to both
// centre and outer edges and also surrounding objects ... align to other objects").
//
// Pure builders on the document. Each moved object keeps its shape; only its position
// changes. A group moves as ONE object, so its box is the box of everything inside it.
// World axes are the canvas axes: X grows right, Y grows DOWN, so "top" is the smaller Y.
import type { Doc } from "../doc/entities";
import { entityBounds } from "../doc/derived";
import { geometryBounds } from "./byNumbers";
import { expandGroupChildren, isLocked, translatePatches } from "./entityOps";

export interface Box {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

type Patch = { id: string; before: Record<string, unknown>; after: Record<string, unknown> };

export type AlignEdge = "left" | "hcenter" | "right" | "top" | "vcenter" | "bottom";
export type Side = "left" | "right" | "above" | "below";

const EPS = 1e-9;

const union = (a: Box | null, b: Box | null): Box | null =>
  !a
    ? b
    : !b
      ? a
      : { minX: Math.min(a.minX, b.minX), minY: Math.min(a.minY, b.minY), maxX: Math.max(a.maxX, b.maxX), maxY: Math.max(a.maxY, b.maxY) };

/**
 * The box of one object as the person sees it: lines, boxes and circles use the drawn
 * geometry (a 3000 wide box is 3000, not 3000 plus the stroke); a group is the box of
 * its parts. Null when the object has no extent (deleted, hidden dimension).
 */
export function objectBox(doc: Doc, id: string): Box | null {
  const e = doc.entities[id];
  if (!e) return null;
  if (e.type === "group") {
    let b: Box | null = null;
    for (const c of e.children) b = union(b, objectBox(doc, c));
    return b;
  }
  const g = geometryBounds(e as never);
  if (g) return g;
  const b = entityBounds(e, doc);
  return b.minX <= b.maxX && b.minY <= b.maxY ? { minX: b.minX, minY: b.minY, maxX: b.maxX, maxY: b.maxY } : null;
}

/** The objects that will move: no locked ones, no object inside a selected group, no repeats. */
export function movableIds(doc: Doc, ids: Iterable<string>, exclude?: string | null): string[] {
  const all = [...new Set(ids)].filter((id) => doc.entities[id] && id !== exclude);
  const inside = new Set<string>();
  for (const id of all) {
    const e = doc.entities[id];
    if (e?.type === "group") for (const d of expandGroupChildren(doc, e.children)) inside.add(d);
  }
  return all.filter((id) => !inside.has(id) && !isLocked(doc, id));
}

/** The box that holds all of these objects. */
export function selectionBox(doc: Doc, ids: Iterable<string>): Box | null {
  let b: Box | null = null;
  for (const id of ids) b = union(b, objectBox(doc, id));
  return b;
}

function moves(doc: Doc, ids: string[], delta: (box: Box, id: string) => { x: number; y: number }): Patch[] {
  const out: Patch[] = [];
  for (const id of ids) {
    const box = objectBox(doc, id);
    if (!box) continue;
    const d = delta(box, id);
    if (Math.abs(d.x) < EPS && Math.abs(d.y) < EPS) continue;
    out.push(...translatePatches(doc, [id], d));
  }
  return out;
}

/**
 * Line one edge or centre of each object up with the same edge or centre of `ref`.
 * With no `ref` the reference is the box around all the objects, which needs two or more.
 */
export function alignPatches(doc: Doc, ids: string[], edge: AlignEdge, ref: Box | null): Patch[] {
  const target = ref ?? (ids.length >= 2 ? selectionBox(doc, ids) : null);
  if (!target) return [];
  return moves(doc, ids, (b) => {
    switch (edge) {
      case "left":
        return { x: target.minX - b.minX, y: 0 };
      case "right":
        return { x: target.maxX - b.maxX, y: 0 };
      case "hcenter":
        return { x: (target.minX + target.maxX) / 2 - (b.minX + b.maxX) / 2, y: 0 };
      case "top":
        return { x: 0, y: target.minY - b.minY };
      case "bottom":
        return { x: 0, y: target.maxY - b.maxY };
      case "vcenter":
        return { x: 0, y: (target.minY + target.maxY) / 2 - (b.minY + b.maxY) / 2 };
    }
  });
}

/** Put the centre of each object on the centre of `ref`, both ways. */
export function centrePatches(doc: Doc, ids: string[], ref: Box | null): Patch[] {
  const target = ref ?? (ids.length >= 2 ? selectionBox(doc, ids) : null);
  if (!target) return [];
  return moves(doc, ids, (b) => ({
    x: (target.minX + target.maxX) / 2 - (b.minX + b.maxX) / 2,
    y: (target.minY + target.maxY) / 2 - (b.minY + b.maxY) / 2
  }));
}

/**
 * Place the objects against one side of `ref`, `gap` apart. The objects move together as
 * one block, so their layout among themselves stays as it is.
 */
export function besidePatches(doc: Doc, ids: string[], side: Side, ref: Box, gap: number): Patch[] {
  const block = selectionBox(doc, ids);
  if (!block) return [];
  const d =
    side === "right"
      ? { x: ref.maxX + gap - block.minX, y: 0 }
      : side === "left"
        ? { x: ref.minX - gap - block.maxX, y: 0 }
        : side === "below"
          ? { x: 0, y: ref.maxY + gap - block.minY }
          : { x: 0, y: ref.minY - gap - block.maxY };
  if (Math.abs(d.x) < EPS && Math.abs(d.y) < EPS) return [];
  return translatePatches(doc, ids, d);
}

// ---- space evenly by a set distance (3.27.0) ------------------------------------------------
// Owner, 2026-10-23: "add a tool so I can select a group of objects and have them spaced evenly with a defined gap
// between outer edges or between the objects' centre lines. In this screenshot I want to change the spacing between
// each object to be wider, pushing the set down." The first object (top, or left) stays; each next one moves along the
// axis so the gap between edges, or the distance between centres, is the value. Objects that sit side by side across
// the axis (a tank and the valve beside it) move together as one UNIT.

export type SpaceMode = "gap" | "pitch";

/** One place along the axis: the objects in it, and its extent on the axis. */
export interface SpaceUnit {
  ids: string[];
  lo: number;
  hi: number;
}

/**
 * The units along `axis`, first to last. Objects whose extents on the axis overlap by half the smaller extent or more
 * are one unit (a valve beside its tank). A small overlap (two tanks that touch) does not join them.
 */
export function spaceUnits(doc: Doc, ids: string[], axis: "h" | "v"): SpaceUnit[] {
  const lo = (b: Box) => (axis === "h" ? b.minX : b.minY);
  const hi = (b: Box) => (axis === "h" ? b.maxX : b.maxY);
  const items = ids
    .map((id) => ({ id, box: objectBox(doc, id) }))
    .filter((i): i is { id: string; box: Box } => i.box !== null)
    .map((i) => ({ id: i.id, lo: lo(i.box), hi: hi(i.box) }))
    .sort((a, b) => a.lo - b.lo || a.hi - b.hi);
  const units: Array<SpaceUnit & { items: Array<{ lo: number; hi: number }> }> = [];
  for (const it of items) {
    const u = units.find((v) =>
      v.items.some((o) => {
        const overlap = Math.min(o.hi, it.hi) - Math.max(o.lo, it.lo);
        return overlap > EPS && overlap >= 0.5 * Math.min(o.hi - o.lo, it.hi - it.lo) - EPS;
      })
    );
    if (u) {
      u.ids.push(it.id);
      u.items.push(it);
      u.lo = Math.min(u.lo, it.lo);
      u.hi = Math.max(u.hi, it.hi);
    } else units.push({ ids: [it.id], lo: it.lo, hi: it.hi, items: [it] });
  }
  return units.sort((a, b) => a.lo - b.lo).map(({ ids: unitIds, lo: l, hi: h }) => ({ ids: unitIds, lo: l, hi: h }));
}

/** The axis the objects are laid along: the one with the larger spread of their centres. */
export function spaceAxis(doc: Doc, ids: string[]): "h" | "v" {
  const boxes = ids.map((id) => objectBox(doc, id)).filter((b): b is Box => b !== null);
  const spread = (c: (b: Box) => number) => (boxes.length ? Math.max(...boxes.map(c)) - Math.min(...boxes.map(c)) : 0);
  return spread((b) => (b.minX + b.maxX) / 2) > spread((b) => (b.minY + b.maxY) / 2) ? "h" : "v";
}

/** The spacing now: the gaps between the edges of next units, and the distances between their centres. */
export function spacingNow(doc: Doc, ids: string[], axis: "h" | "v"): { units: number; gaps: number[]; pitches: number[] } {
  const u = spaceUnits(doc, ids, axis);
  const gaps: number[] = [];
  const pitches: number[] = [];
  for (let i = 1; i < u.length; i++) {
    gaps.push(u[i]!.lo - u[i - 1]!.hi);
    pitches.push((u[i]!.lo + u[i]!.hi) / 2 - (u[i - 1]!.lo + u[i - 1]!.hi) / 2);
  }
  return { units: u.length, gaps, pitches };
}

/**
 * Space the objects along `axis` by `value`: the gap between the edges of next units ("gap"), or the distance between
 * their centres ("pitch"). The first unit stays. A string says why nothing moves: fewer than two units, a gap below 0,
 * or a centre distance that makes two units overlap.
 */
export function spacePatches(doc: Doc, ids: string[], axis: "h" | "v", mode: SpaceMode, value: number): Patch[] | string {
  const units = spaceUnits(doc, ids, axis);
  if (units.length < 2) return "Space evenly needs two or more objects, one after the other along the line.";
  if (!Number.isFinite(value)) return "Type a distance.";
  if (mode === "gap" && value < 0) return "The gap cannot be less than 0.";
  if (mode === "pitch") {
    for (let i = 1; i < units.length; i++) {
      const need = (units[i - 1]!.hi - units[i - 1]!.lo) / 2 + (units[i]!.hi - units[i]!.lo) / 2;
      if (value < need - EPS) return `A centre distance of ${round(value)} mm makes two objects overlap. The smallest distance for these objects is ${round(need)} mm.`;
    }
  }
  const out: Patch[] = [];
  let cursor = units[0]!.hi; // gap: the far edge of the last placed unit
  let centre = (units[0]!.lo + units[0]!.hi) / 2; // pitch: the centre of the last placed unit
  for (let i = 1; i < units.length; i++) {
    const u = units[i]!;
    const size = u.hi - u.lo;
    const newLo = mode === "gap" ? cursor + value : centre + value - size / 2;
    const d = newLo - u.lo;
    if (Math.abs(d) > EPS) out.push(...translatePatches(doc, u.ids, axis === "h" ? { x: d, y: 0 } : { x: 0, y: d }));
    cursor = newLo + size;
    centre = newLo + size / 2;
  }
  return out;
}

const round = (v: number): string => (Math.round(v * 10) / 10).toString();

/**
 * Spread three or more objects so the gaps between them are equal. The two outer
 * objects stay where they are. axis "h" spreads left to right; "v" spreads top to bottom.
 */
export function distributePatches(doc: Doc, ids: string[], axis: "h" | "v"): Patch[] {
  const items = ids
    .map((id) => ({ id, box: objectBox(doc, id) }))
    .filter((i): i is { id: string; box: Box } => i.box !== null);
  if (items.length < 3) return [];
  const lo = (b: Box) => (axis === "h" ? b.minX : b.minY);
  const hi = (b: Box) => (axis === "h" ? b.maxX : b.maxY);
  items.sort((a, b) => lo(a.box) - lo(b.box));
  const span = Math.max(...items.map((i) => hi(i.box))) - lo(items[0]!.box);
  const used = items.reduce((s, i) => s + (hi(i.box) - lo(i.box)), 0);
  const gap = (span - used) / (items.length - 1);
  const out: Patch[] = [];
  let cursor = lo(items[0]!.box);
  for (const it of items) {
    const d = cursor - lo(it.box);
    if (Math.abs(d) > EPS) out.push(...translatePatches(doc, [it.id], axis === "h" ? { x: d, y: 0 } : { x: 0, y: d }));
    cursor += hi(it.box) - lo(it.box) + gap;
  }
  return out;
}
